import { useState } from "react";
import { Grid } from "@mui/material";

import {
  FormTitle,
  DatePickerField,
  Text,
  SelectField,
  ReadOnlyTextField,
} from "ui-components/Form";
import MDBox from "components/MDBox";
import { useCustWhOptionsQuery } from "hooks/useQueryHook";
import { useZoneOptionsQuery } from "hooks/useZoneQuery";
import { useLocationOptionsQuery } from "hooks/useLocationQuery";
import { getPackTypeUOM } from "services/data.internal.service";
import { useQuery } from "@tanstack/react-query";
import { conditionOptions } from "layouts/views/common/options";
import { numberValidation } from "layouts/views/common/validation";
import RecvSerialNumber from "../../receiving/components/RecvSerialNumber";
import { useFieldArray } from "react-hook-form";

function POReceivingDetail({
  detailRender,
  values,
  control,
  errors,
  setValue,
  getValues,
  mode = "ADD",
  serialAlert
}) {
  const { skuCode, customerCode, vendorCode, recvWh, recvZone } = values;
  const [status, mlnCtrl, manualLotCtrl, manDateCtrl, expiryDateCtrl, inbSerialNbrCtrl, serialNumbers, quantity] = getValues([
    "status",
    "mlnCtrl",
    "manualLotCtrl",
    "manDateCtrl",
    "expiryDateCtrl",
    "inbSerialNbrCtrl",
    "serialNumbers",
    "quantity",
  ]);
  const { fields,append,remove } = useFieldArray({ name: "serialNumbers", control });
  const [uomOptions, setUOMOptions] = useState([]);
  const [whOptions, setWhOptions] = useState([]);
  const [zoneOptions, setZoneOptions] = useState([]);
  const [locOptions, setLocOptions] = useState([]);
  // useEffect(() => {
  //   if (status !== "Open" || qtyReceived === 0 || uom === null) return;
  //   const quantity = qtyReceived * (uom ? uom.value : 0);
  //   setValue("quantity", quantity);
  // }, [qtyReceived, uom]);
  const enabled = Boolean(skuCode && customerCode && vendorCode && status === "Open");
  const onQtyChange = (e) => {
    const { value } = e.target;
    const qty = numberValidation(value);
    const uom = getValues("uom");
    const uomQty = uom ? uom.value : 0;
    setValue("qtyReceived", qty, { shouldDirty: true });
    const quantity = qty * uomQty
    setValue("quantity", quantity);

    if (inbSerialNbrCtrl && quantity != serialNumbers.length){
      const filteredSerial = serialNumbers.filter(x => Boolean(x.serialNumber));
      if (quantity > filteredSerial.length){
        const emptyItems = Array.from({ length: quantity - filteredSerial.length }, () => ({ serialNumber: "", newItem: true, active: true }));
        setValue("serialNumbers", [...filteredSerial, ...emptyItems]);
      }
      else {
        setValue("serialNumbers", [...filteredSerial])
      }
    }
  };
  const onUOMChange = (e, option) => {
    setValue("uom", option, { shouldDirty: true });
    const uomQty = option ? option.value : 0;
    const qty = getValues("qtyReceived");
    const quantity = qty * uomQty
    setValue("quantity", quantity);

    if (inbSerialNbrCtrl && quantity != serialNumbers.length){
      const filteredSerial = serialNumbers.filter(x => Boolean(x.serialNumber));
      if (quantity > filteredSerial.length){
        const emptyItems = Array.from({ length: quantity - filteredSerial.length }, () => ({ serialNumber: "", newItem: true, active: true }));
        setValue("serialNumbers", [...filteredSerial, ...emptyItems]);
      }
      else {
        setValue("serialNumbers", [...filteredSerial])
      }
    }
  };
  useQuery({
    queryKey: ["pack-type-uom", { skuCode, customerCode, vendorCode }],
    queryFn: () => getPackTypeUOM({ skuCode, customerCode, vendorCode }),
    enabled,
    onSuccess: (data) => {
      const { packType, baseUOM, uomOptions: options } = data.data.data;
      setValue("packType", packType);
      setValue("baseUOM", baseUOM);
      setUOMOptions(options.map((x) => ({ value: x.upm, label: x.uom })));
    },
  });
  useCustWhOptionsQuery({
    customerCode,
    onSuccess: (data) => setWhOptions(data.data.data),
  });
  useZoneOptionsQuery({
    enabled: Boolean(recvWh && status === "Open"),
    whCode: recvWh,
    zoneType: "staging",
    onSuccess: (data) => setZoneOptions(data.data.data),
  });
  useLocationOptionsQuery({
    enabled: Boolean(recvWh && recvZone && status === "Open"),
    whCode: recvWh,
    zoneID: recvZone,
    onSuccess: (data) => setLocOptions(data.data.data),
  });
  const onZoneChange = (e, option) => {
    setLocOptions([]);
    setValue("recvZone", option);
    setValue("recvLocation", null);
  };
  const onWarehouseChange = (e, option) => {
    setZoneOptions([]);
    setLocOptions([]);
    setValue("recvWh", option);
    setValue("recvZone", null);
    setValue("recvLocation", null);
  };
  function renderFn(property, index, length) {
    const { size, disabled, editable, xs: xs2, ...data } = property;
    const xs = xs2 ?? 6 / 2;
    const fieldProps = {
      ...data,
      control,
      errors,
      ...(disabled && { disabled }),
      // ...(mode === "EDIT" &&
      //   newItem === false &&
      //   editable === false && { disabled: true, type: "text" }),

      //...(mode === "EDIT" && values.status === "Cancel" && { disabled: true }),
      ...(data.name === "qtyReceived" && { onChange: onQtyChange }),
      ...(status !== "Open" && data.name !== "serialNumbers" && { type: "readonly" }),
      ...(data.name === "manufacturingDate" && Boolean(manDateCtrl) && { required: true }),
      ...(data.name === "mln" && Boolean(mlnCtrl) && { required: true }),
      ...(data.name === "manualLot" && Boolean(manualLotCtrl) && { required: true }),
      ...(data.name === "expiryDate" && Boolean(expiryDateCtrl) && { required: true }),
    };
    let renderField;
    switch (fieldProps.type) {
      case "text":
      case "number":
        renderField = <Text field={fieldProps} />;
        break;
      case "readonly":
        renderField = <ReadOnlyTextField field={fieldProps} />;
        break;
      case "select":
        let options = [];
        let onChange = null;
        switch (data.name) {
          case "recvWh":
            options = whOptions;
            onChange = onWarehouseChange;
            break;
          case "recvZone":
            options = zoneOptions;
            onChange = onZoneChange;
            break;
          case "recvLocation":
            options = locOptions;
            break;
          case "uom":
            options = uomOptions;
            onChange = onUOMChange;
            break;
          case "condition":
            options = conditionOptions;
            break;
        }
        renderField = <SelectField field={fieldProps} options={options} onChange={onChange} />;
        break;
      case "date":
        renderField = <DatePickerField field={fieldProps} />;
        break;
        case "multi-select":
          switch (data.name) {
            case "serialNumbers":
              renderField = Boolean(inbSerialNbrCtrl) ? (
                <RecvSerialNumber
                  data={fields}
                  append={append}
                  remove={remove}
                  control={control}
                  errors={errors}
                  serialNumbers={serialNumbers}
                  quantity={quantity}
                  alert={serialAlert}
                  setValue={setValue}
                />
              ) : (
                <></>
              );
              break;
          }
          break;
    }
    return (
      <Grid key={index} item xs={xs}>
        {renderField}
      </Grid>
    );
  }
  return (
    <MDBox>
      <FormTitle title={mode === "EDIT" ? "Edit PO Receiving Detail" : "Add PO Receiving Detail"} />
      <MDBox mt={1.625}>
        {detailRender.map((rows, i) => (
          <Grid container spacing={3} key={i}>
            {rows.map((row, index) => renderFn(row, index, rows.length))}
          </Grid>
        ))}
      </MDBox>
    </MDBox>
  );
}

export default POReceivingDetail;
